/* eslint-disable no-param-reassign */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { gql } from 'graphql-request';
import PropTypes from 'prop-types';

import {
  BackButton,
  Error,
  InfoManufacturingOrder,
  Loading,
  MMSDashboardRenderingComponent,
  ViewTitle,
} from '@iarcpsu/emanufac-components/src/components';
import { app as appConfig } from '../../../configs';
import graphqlClient from '../../../configs/graphqlClient';
import * as actions from '../../../redux/actions';

/**
 * @function CustomDetailManufacturingOrder
 * @description Display a collections or a list of MainDetailMO from database
 */

export default function CustomDetailManufacturingOrder({ title, subtitle }) {
  const dispatch = useDispatch();
  const params = useParams();
  const manufacturingOrder = useSelector((state) => state.manufacturingOrder);
  const information = useSelector((state) => state.information);
  const mmsDashboard = useSelector((state) => state.mmsDashboard);

  const me = useSelector((state) => state.me);

  const findMainDetailMO = gql`
    query FindMainDetailMO(
      $input: ManufacturingOrderInput
      $paymentTypeInput: PaymentTypeInput
      $customerInput: CustomerInput
      $mmsDashboardInput: MMSDashboardInput
    ) {
      findOneMMSDashboard(input: $mmsDashboardInput) {
        _id
        name
        is_mo_level
        elements {
          index
          dashboard_element {
            _id
            name
            dashboard_type
            is_mo_level
            elements {
              name
              index
              source_type
              resulting_function_type_source {
                _id
                name
              }
              measurement_type_source {
                _id
                name
                data_type
                label_left
                label_right
              }
            }
          }
          height
          width
        }
      }
      findOneManufacturingOrder(input: $input) {
        _id
        running_number
        prefix
        start_date
        expected_date
        completed
        deleted
        remark
        delete_note
        customer {
          type_code
          name
        }
        assigner {
          firstname
          lastname
          image {
            url
          }
        }
        process {
          _id
          deleted
          completed
          product {
            _id
            type_code
            name
          }
          produce_material_instead
          product_as_material {
            _id
            type_code
            name
          }
          machine {
            _id
            name
          }
          steps {
            _id
            name
            index
            measurements {
              _id
              value
              additional_value
              boolean_value
              date_value
              measurement_type {
                _id
                name
                description
                data_type
              }
            }
            resultingFunctionTypes {
              _id
              name
              is_process_level
              value
              executor {
                index
                using_constant
                using_static_value
                static_value
                using_another_rft
                operation
                measurement_type {
                  _id
                  name
                  data_type
                  type_code
                }
                constant {
                  _id
                  name
                  type_code
                  value
                  data_type
                  unit {
                    name
                  }
                }
                another_rft {
                  _id
                  name
                  is_process_level
                  executor {
                    index
                    using_constant
                    using_static_value
                    using_another_rft
                    static_value
                    operation
                    measurement_type {
                      _id
                      name
                      data_type
                      type_code
                    }
                    constant {
                      _id
                      name
                      type_code
                      value
                      data_type
                      unit {
                        name
                      }
                    }
                  }
                }
              }
            }
            completed
            deleted
          }
          resulting_function_types {
            _id
            name
            is_process_level
            value
            executor {
              index
              using_constant
              using_static_value
              static_value
              using_another_rft
              operation
              measurement_type {
                _id
                name
                data_type
                type_code
              }
              constant {
                _id
                name
                type_code
                value
                data_type
                unit {
                  name
                }
              }
              another_rft {
                _id
                name
                is_process_level
                executor {
                  index
                  using_constant
                  using_static_value
                  static_value
                  using_another_rft
                  operation
                  measurement_type {
                    _id
                    name
                    data_type
                    type_code
                  }
                  constant {
                    _id
                    name
                    type_code
                    value
                    data_type
                    unit {
                      name
                    }
                  }
                }
              }
            }
          }
          customer {
            name
            _id
            type_code
          }
          completed
          deleted
        }
      }

      findPaymentTypes(input: $paymentTypeInput) {
        rows {
          _id
          name
        }
      }
      findCustomers(input: $customerInput) {
        rows {
          _id
          type_code
          name
        }
      }
    }
  `;

  const queryDataFromServer = async () => {
    dispatch(actions.manufacturingOrderReset());
    try {
      const queryResult = await graphqlClient.request(findMainDetailMO, {
        input: {
          enableFetchInside: false,
          fetchCurrentStep: false,
          fetchCustomer: false,
          fetchMaterial: false,
          fetchProduct: true,
          id: params.id,
          fetchProcess: false,
        },
        paymentTypeInput: {
          page: 1,
          size: appConfig.maxFetchSize,
        },
        customerInput: {
          page: 1,
          size: appConfig.maxFetchSize,
        },
        mmsDashboardInput: {
          id: params.dashboardId,
        },
      });
      const manufacturingOrderData = queryResult?.findOneManufacturingOrder;
      const paymentTypeData = queryResult?.findPaymentTypes;
      const customerData = queryResult?.findCustomers;
      const mmsDashboardData = queryResult?.findOneMMSDashboard;
      dispatch(actions.manufacturingOrderStateOneSet(manufacturingOrderData));
      dispatch(actions.paymentTypeStateSet(paymentTypeData));
      dispatch(actions.customerStateSet(customerData));
      dispatch(actions.mmsDashboardStateOneSet(mmsDashboardData));
      const insideQueryResult = await graphqlClient.request(findMainDetailMO, {
        input: {
          enableFetchInside: true,
          fetchCurrentStep: true,
          fetchCustomer: true,
          fetchMaterial: false,
          fetchProduct: true,
          id: params.id,
          fetchProcess: true,
        },
        paymentTypeInput: {
          page: 1,
          size: appConfig.maxFetchSize,
        },
        customerInput: {
          page: 1,
          size: appConfig.maxFetchSize,
        },
      });
      const insideMOData = insideQueryResult?.findOneManufacturingOrder;
      dispatch(actions.manufacturingOrderStateOneSet(insideMOData));
    } catch (err) {
      console.error('Error On Fetch Inside Process', err);
    }
  };

  useEffect(() => {
    queryDataFromServer();
    return () => {};
  }, [params]);

  const handleMOToSuccess = async ({
    manufacturingOrderId,
    handleSuccess = () => {},
  }) => {
    try {
      await dispatch(
        actions.manufacturingOrderPut(manufacturingOrderId, {
          completed: true,
        }),
      );
      handleSuccess();
      queryDataFromServer();
    } catch (error) {
      window.alert(`ไม่สามารถปิดคำสั่งผลิตได้ ${error?.message}`);
      console.error('Handle Manufacturing Order Success Fail', error);
    }
  };

  const handleMOToCancle = async ({
    manufacturingOrderId,
    handleSuccess = () => {},
  }) => {
    try {
      await dispatch(
        actions.manufacturingOrderPut(manufacturingOrderId, { deleted: true }),
      );
      handleSuccess();
      queryDataFromServer();
    } catch (error) {
      window.alert(`ไม่สามารถยกเลิกคำสั่งผลิตได้ ${error?.message}`);
      console.error('Handle Manufacturing Order Cancle Fail', error);
    }
  };
  const renderTitle = () => <ViewTitle title={title} subtitle={subtitle} />;

  if (manufacturingOrder.isLoading || mmsDashboard.isLoading) {
    return <Loading />;
  }
  if (
    !manufacturingOrder.isLoading &&
    manufacturingOrder.isCompleted &&
    !mmsDashboard.isLoading &&
    mmsDashboard.isCompleted
  ) {
    return (
      <div>
        <div className="flex justify-between">
          {renderTitle()}
          <div className="self-center">
            <Link to={`/mms/manufacturing-orders/edit/${params.id}`}>
              <Button color="warning" variant="contained">
                แก้ไข
              </Button>
            </Link>
          </div>
        </div>
        <div className="my-2">
          <BackButton />
        </div>
        <div className="my-2">
          <InfoManufacturingOrder
            information={information}
            manufacturingOrder={manufacturingOrder}
            me={me}
            handleMOToCancle={handleMOToCancle}
            handleMOToSuccess={handleMOToSuccess}
          />
        </div>
        {/** Suppose Must be the same product and the same step */}
        <div className="my-2">
          <MMSDashboardRenderingComponent
            mmsDashboard={mmsDashboard}
            isExample={false}
            isMoLevel={mmsDashboard?.is_mo_level}
            manufacturingOrder={manufacturingOrder}
          />
        </div>
      </div>
    );
  }
  return <Error />;
}

CustomDetailManufacturingOrder.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

CustomDetailManufacturingOrder.defaultProps = {
  title: '',
  subtitle: '',
};
